import Footer from '../components/Footer';
import Navbar from '../components/Navbar';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#FFFBF7] text-[#5F5650]">
      <Navbar />
      <main className="px-4 py-12 md:px-8">
        <article className="mx-auto w-full max-w-[800px] rounded-2xl bg-white p-6 shadow-sm md:p-10">
          <h1 className="font-[Nunito] text-4xl font-extrabold text-[#5B3FA6]">PottyPal Privacy Policy</h1>
          <p className="mt-2 text-sm text-[#7B716A]">Last updated: April 2026</p>
          <p className="text-sm text-[#7B716A]">
            Support:{' '}
            <a className="text-[#5B3FA6] underline" href="mailto:support@logicalflows.com">
              support@logicalflows.com
            </a>
          </p>

          <section className="mt-8 space-y-6 leading-relaxed">
            <div>
              <h2 className="font-[Nunito] text-2xl font-bold text-[#4A3F66]">1. INTRODUCTION</h2>
              <p className="mt-2">
                PottyPal (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) is operated by LogicalFlows. We are committed
                to protecting the privacy of parents and their children who use our mobile application (&quot;PottyPal&quot;
                or &quot;the App&quot;).
              </p>
              <p className="mt-2">
                This Privacy Policy explains what information we collect, how we use it, and the choices you have regarding
                your information.
              </p>
            </div>

            <div>
              <h2 className="font-[Nunito] text-2xl font-bold text-[#4A3F66]">2. INFORMATION WE COLLECT</h2>
              <h3 className="mt-3 font-[Nunito] text-xl font-bold text-[#4A3F66]">2.1 Information You Provide Directly:</h3>
              <ul className="mt-2 list-disc space-y-1 pl-6">
                <li>Account information: your name and email address</li>
                <li>Baby profile: your child&apos;s name, date of birth, gender</li>
                <li>Training data: potty logs, training status, challenges, temperament</li>
                <li>App preferences: notification settings, language, daily routine times</li>
              </ul>

              <h3 className="mt-4 font-[Nunito] text-xl font-bold text-[#4A3F66]">2.2 Information Collected Automatically:</h3>
              <ul className="mt-2 list-disc space-y-1 pl-6">
                <li>Device information: device type, operating system version</li>
                <li>App usage: features used, session duration, crash reports</li>
                <li>Push notification token (for sending reminders)</li>
              </ul>

              <h3 className="mt-4 font-[Nunito] text-xl font-bold text-[#4A3F66]">2.3 Information We Do NOT Collect:</h3>
              <ul className="mt-2 list-disc space-y-1 pl-6">
                <li>We do not collect your child&apos;s precise location</li>
                <li>We do not collect payment card details (handled by the App Store / Google Play)</li>
                <li>We do not collect biometric data</li>
              </ul>
            </div>

            <div>
              <h2 className="font-[Nunito] text-2xl font-bold text-[#4A3F66]">3. HOW WE USE YOUR INFORMATION</h2>
              <p className="mt-2">We use the information we collect to:</p>
              <ul className="mt-2 list-disc space-y-1 pl-6">
                <li>Provide and personalize the PottyPal service</li>
                <li>Generate AI-powered coaching tips using Google Gemini AI</li>
                <li>Send potty reminder notifications at times you configure</li>
                <li>Track your child&apos;s training progress and generate insights</li>
                <li>Improve our app features and fix bugs</li>
                <li>
                  Respond to your support requests at{' '}
                  <a className="text-[#5B3FA6] underline" href="mailto:support@logicalflows.com">
                    support@logicalflows.com
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="font-[Nunito] text-2xl font-bold text-[#4A3F66]">4. HOW WE SHARE YOUR INFORMATION</h2>
              <p className="mt-2">We do not sell your personal information. We share data only with:</p>

              <h3 className="mt-3 font-[Nunito] text-xl font-bold text-[#4A3F66]">4.1 Service Providers:</h3>
              <ul className="mt-2 list-disc space-y-1 pl-6">
                <li>Google Firebase: secure data storage, authentication, push notifications</li>
                <li>
                  Google Gemini AI: anonymized coaching requests (child&apos;s name and stats only, no identifying information
                  shared)
                </li>
                <li>
                  Google AdMob: advertising for free-tier users (no child data shared with advertisers; mixed-audience app
                  rules apply)
                </li>
                <li>RevenueCat: subscription and purchase management only</li>
              </ul>

              <h3 className="mt-4 font-[Nunito] text-xl font-bold text-[#4A3F66]">4.2 Legal Requirements:</h3>
              <p className="mt-2">
                We may disclose information if required by law or to protect rights and safety.
              </p>
            </div>

            <div>
              <h2 className="font-[Nunito] text-2xl font-bold text-[#4A3F66]">5. CHILDREN&apos;S PRIVACY (COPPA COMPLIANCE)</h2>
              <p className="mt-2">PottyPal is designed for parents to use on behalf of their children under 13.</p>
              <ul className="mt-2 list-disc space-y-1 pl-6">
                <li>We do not knowingly collect personal information directly from children</li>
                <li>All child data is entered and controlled by the parent account holder</li>
                <li>Child Mode features do not collect any additional personal data</li>
                <li>No advertising is shown in the Child Mode section of the app</li>
                <li>Parents can view, export, or delete all child-related data at any time</li>
              </ul>
              <p className="mt-2">
                If you believe we have inadvertently collected information from a child without parental consent, please
                contact us at{' '}
                <a className="text-[#5B3FA6] underline" href="mailto:support@logicalflows.com">
                  support@logicalflows.com
                </a>{' '}
                and we will delete it promptly.
              </p>
            </div>

            <div>
              <h2 className="font-[Nunito] text-2xl font-bold text-[#4A3F66]">6. DATA SECURITY</h2>
              <ul className="mt-2 list-disc space-y-1 pl-6">
                <li>All data is encrypted in transit using TLS 1.3</li>
                <li>All data is encrypted at rest using Google Firebase security standards</li>
                <li>We use Firebase Authentication - we never store raw passwords</li>
                <li>Access to production data is restricted to authorized personnel only</li>
              </ul>
            </div>

            <div>
              <h2 className="font-[Nunito] text-2xl font-bold text-[#4A3F66]">7. DATA RETENTION</h2>
              <p className="mt-2">
                We retain your data for as long as your account is active. If you delete your account, all associated data is
                permanently deleted within 30 days.
              </p>
            </div>

            <div>
              <h2 className="font-[Nunito] text-2xl font-bold text-[#4A3F66]">8. YOUR RIGHTS AND CHOICES</h2>
              <p className="mt-2">You have the right to:</p>
              <ul className="mt-2 list-disc space-y-1 pl-6">
                <li>Access: View all your data within the app</li>
                <li>Export: Download your data (Settings → Data &amp; Privacy → Export Data)</li>
                <li>Correct: Update your information in the app Settings</li>
                <li>
                  Delete: Delete your account and all data (Settings → Data &amp; Privacy → Delete Account)
                </li>
                <li>Opt-out: Disable analytics data collection in Settings</li>
                <li>Notification control: Turn off any notification type in Settings</li>
              </ul>
            </div>

            <div>
              <h2 className="font-[Nunito] text-2xl font-bold text-[#4A3F66]">9. THIRD-PARTY SERVICES</h2>
              <p className="mt-2">Our app uses:</p>
              <ul className="mt-2 list-disc space-y-1 pl-6">
                <li>
                  Google Firebase (privacy policy:{' '}
                  <a className="text-[#5B3FA6] underline" href="https://firebase.google.com/support/privacy">
                    firebase.google.com/support/privacy
                  </a>
                  )
                </li>
                <li>
                  Google Gemini AI (privacy policy:{' '}
                  <a className="text-[#5B3FA6] underline" href="https://ai.google.dev/terms">
                    ai.google.dev/terms
                  </a>
                  )
                </li>
                <li>
                  Google AdMob (privacy policy:{' '}
                  <a className="text-[#5B3FA6] underline" href="https://policies.google.com/privacy">
                    policies.google.com/privacy
                  </a>
                  )
                </li>
                <li>
                  RevenueCat (privacy policy:{' '}
                  <a className="text-[#5B3FA6] underline" href="https://www.revenuecat.com/privacy">
                    revenuecat.com/privacy
                  </a>
                  )
                </li>
              </ul>
            </div>

            <div>
              <h2 className="font-[Nunito] text-2xl font-bold text-[#4A3F66]">10. CHANGES TO THIS POLICY</h2>
              <p className="mt-2">
                We may update this Privacy Policy from time to time. We will notify you of significant changes via a
                notification in the app or by email. Continued use of the app after changes constitutes acceptance.
              </p>
            </div>

            <div>
              <h2 className="font-[Nunito] text-2xl font-bold text-[#4A3F66]">11. CONTACT US</h2>
              <p className="mt-2">If you have questions about this Privacy Policy or your data:</p>
              <p className="mt-2">
                Email:{' '}
                <a className="text-[#5B3FA6] underline" href="mailto:support@logicalflows.com">
                  support@logicalflows.com
                </a>
              </p>
              <p>Response time: within 2 business days</p>
              <p className="mt-2">LogicalFlows</p>
              <p>
                <a className="text-[#5B3FA6] underline" href="mailto:support@logicalflows.com">
                  support@logicalflows.com
                </a>
              </p>
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </div>
  );
}
